import { THEME } from "@digitallab/grid-common-frontend-components";
import { react18WorkaroundProps } from "../../utils/constants/constants";
import { OwcButton, OwcIcon, OwcModalDialog, OwcProgressSpinner, OwcSlideToggle, OwcTooltip } from "@one/react";
import { CodeResponse, googleLogout, useGoogleLogin } from "@react-oauth/google";
// @ts-ignore
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import { DURATION } from "../../constants";
import { ComponentType, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { DISCONNECT_GOOGLE_CALENDAR, GET_GOOGLE_CALENDAR } from "../../gql/bookingapi";
import { withApollo, WithApolloClient } from "react-apollo";
import { connect } from "react-redux";
import { compose } from "redux";
import { UserDetailsTypes } from "../../types/UserDetailsTypes";
import { GoogleCalendarMutationResponse } from "../../types/GoogleCalendarTypes";
import { loadUserInfo as loadUserInfoAction } from "../../features/user/redux/actions";
import {
  saveGoogleCalendarConnectionData,
  clearGoogleCalendarData,
  getGoogleCalendarConnectionData,
  isGoogleCalendarConnected,
} from "../../utils/store/googleCalendarPersistence";
import GoogleCalenderIcon from "../../icons/GoogleCalenderIcon";

const tooltipMessageForSynced =
  "Once synchronization is enabled, all booking events you participate in wil be displayed in your Google Calendar. Status: Enabled";

const tooltipMessageForNotSynced =
  "Once synchronization is enabled, all booking events you participate in wil be displayed in your Google Calendar. Status: Disabled";

const SNACKBAR_MESSAGES = {
  successLink: "Your account is now linked to Google Calendar!",
  successUnlink: "Your account is now unlinked from Google Calendar!",
  errorLogin: "There was an error with Google Calendar login",
  errorLogout: "There was an error with Google Calendar logout",
  accessDenied: "Google Calendar access was denied. Please try again and make sure to grant calendar permissions.",
  insufficientPermission:
    "Insufficient permissions granted. Please try again and make sure to grant calendar access permissions.",
  networkError: "Network error occurred. Please check your connection and try again.",
  popupClosed: "Google Calendar popup was closed, before the login was finished. Please try again.",
  popupFailedToOpen: "Google Calendar popup failed to open. Please try again.",
  invalidClient: "Authentication configuration error. Please contact your administrator.",
};

const showGoogleErrorSnackbar = (message: string) => {
  snackbarService.show({
    message: message,
    type: "error",
    duration: DURATION,
  });
};

const showGoogleSuccessSnackbar = (message: string) => {
  snackbarService.show({
    message: message,
    type: "success",
    duration: DURATION,
  });
};

type LoggedInType = "initial" | "true" | "false" | "error" | "loading";

const mapStateToProps = (state: { user: UserDetailsTypes }) => ({
  user: state.user,
});

const GoogleCalendarSwitch = ({
  client,
  user,
  loadUserInfo,
  showCalendarIcon,
}: {
  client: WithApolloClient<any>;
  user: UserDetailsTypes;
  loadUserInfo: (user: UserDetailsTypes) => void;
  showCalendarIcon?: boolean;
}) => {
  const [loggedIn, setLoggedIn] = useState<LoggedInType>("initial");
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [isToggleChecked, setIsToggleChecked] = useState<boolean>(false);

  useEffect(() => {
    const connected = !!user.googleCalendarId;

    const localStorageConnected = isGoogleCalendarConnected();
    const isConnected = connected || localStorageConnected;

    setIsToggleChecked(isConnected);
    setLoggedIn(isConnected ? "true" : "false");

    if (localStorageConnected && !connected) {
      const storedData = getGoogleCalendarConnectionData();
      if (storedData.calendarId) {
        loadUserInfo({
          ...user,
          googleCalendarId: storedData.calendarId,
        });
      }
    }
  }, [user, loadUserInfo]);

  const handleLogout = useCallback(async (showSnackbar: boolean = true) => {
    try {
      await client.mutate({
        mutation: DISCONNECT_GOOGLE_CALENDAR,
        variables: { action: "disconnect" },
        fetchPolicy: "no-cache",
      });

      googleLogout();

      // Clear all Google Calendar data
      clearGoogleCalendarData();
      sessionStorage.removeItem('googleCalendar_selectedUsers');

      loadUserInfo({
        ...user,
        googleCalendarId: "",
      });

      // Trigger storage event for other components to react
      window.dispatchEvent(new Event('storage'));

      if (showSnackbar) {
        showGoogleSuccessSnackbar(SNACKBAR_MESSAGES.successUnlink);
      }
      setLoggedIn("false");
      setIsToggleChecked(false);
    } catch (error) {
      if (showSnackbar) {
        showGoogleErrorSnackbar(SNACKBAR_MESSAGES.errorLogout);
        setLoggedIn("true");
      } else {
        // When called due to auth error (showSnackbar=false), always untoggle
        setLoggedIn("false");
        setIsToggleChecked(false);
        loadUserInfo({
          ...user,
          googleCalendarId: "",
        });
      }
    }
  }, [client, user, loadUserInfo]);

  // Listen for storage changes (e.g., when tokens are cleared due to auth errors)
  useEffect(() => {
    const handleStorageChange = () => {
      const localStorageConnected = isGoogleCalendarConnected();
      
      // If tokens were cleared, perform full logout without snackbar
      if (!localStorageConnected && isToggleChecked) {
        handleLogout(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isToggleChecked, handleLogout]);

  const handleGoogleError = (error: any, errorMessage: string = SNACKBAR_MESSAGES.errorLogin) => {
    console.error(error);
    setLoggedIn("false");
    if (errorMessage.includes("access_denied")) {
      showGoogleErrorSnackbar(SNACKBAR_MESSAGES.accessDenied);
    } else if (
      [
        "insufficient_permission",
        "insufficient permission",
        "insufficient_scope",
        "insufficientpermissions"
      ].some((term) => errorMessage.includes(term))
    ) {
      showGoogleErrorSnackbar(SNACKBAR_MESSAGES.insufficientPermission);
    } else if (errorMessage.includes("network_error")) {
      showGoogleErrorSnackbar(SNACKBAR_MESSAGES.networkError);
    } else if (errorMessage.includes("popup_closed")) {
      showGoogleErrorSnackbar(SNACKBAR_MESSAGES.popupClosed);
    } else if (errorMessage.includes("popup_failed_to_open")) {
      showGoogleErrorSnackbar(SNACKBAR_MESSAGES.popupFailedToOpen);
    } else if (errorMessage) {
      const message = errorMessage.replace("GraphQL error: ", "");
      showGoogleErrorSnackbar(message);
    } else {
      showGoogleErrorSnackbar(SNACKBAR_MESSAGES.errorLogin);
    }
  };

  const handleLogin = useGoogleLogin({
    flow: "auth-code",
    scope: "https://www.googleapis.com/auth/calendar",
    onSuccess: async (codeResponse: CodeResponse) => {
      try {
        const response = await client.mutate({
          mutation: GET_GOOGLE_CALENDAR,
          variables: { code: codeResponse.code },
          fetchPolicy: "no-cache",
        });
        const data: GoogleCalendarMutationResponse = response?.data?.connectGoogleCalendar;
        const error = response?.data?.connectGoogleCalendar?.error;
        if (error) {
          handleGoogleError(error?.toLowerCase())
          return;
        }

        saveGoogleCalendarConnectionData({
          calendarId: data?.calendarId,
          accessToken: data?.accessToken,
          refreshToken: data?.refreshToken,
          expiryDate: data?.expiryDate,
          tokenType: data?.tokenType,
          scope: data?.scope,
        });

        loadUserInfo({
          ...user,
          googleCalendarId: data?.calendarId,
        });

        setLoggedIn("true");
        setIsToggleChecked(true);
        showGoogleSuccessSnackbar(SNACKBAR_MESSAGES.successLink);
      } catch (error: unknown) {
        handleGoogleError(error, error instanceof Error ? error.message.toLowerCase() : undefined);
      }
    },
    onError: async (errorResponse: Pick<CodeResponse, "error" | "error_description" | "error_uri">) => {
      handleGoogleError(errorResponse, errorResponse?.error?.toLowerCase());
      setLoggedIn("false");
    },
    onNonOAuthError: async (error) => {
      setLoggedIn("false");
      handleGoogleError(error?.type?.toLowerCase());
    },
  });

  return (
    <div
      data-testid="google-calendar-toggle-wrapper"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "1",
      }}
    >
      {["loading", "initial"].includes(loggedIn as LoggedInType) ? (
        <OwcProgressSpinner
          style={{
            height: THEME["one-spacer-16"],
            width: THEME["one-spacer-16"],
            marginRight: THEME["one-spacer-8"],
          }}
          diameter={16}
          {...react18WorkaroundProps}
        />
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: THEME["one-spacer-8"],
            flex: "1",
            justifyContent: "space-between",
          }}
        >
          <OwcSlideToggle
            data-testid="google-calendar-toggle"
            checked={isToggleChecked}
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();

              if (isToggleChecked) {
                setShowDialog(true);
                return;
              }

              setLoggedIn("loading");
              handleLogin();
            }}
            {...react18WorkaroundProps}
          >
            Sync to Google Calendar
          </OwcSlideToggle>
          <OwcTooltip
            title={user.googleCalendarId ? tooltipMessageForSynced : tooltipMessageForNotSynced}
            anchor={"icon-warning"}
            {...react18WorkaroundProps}
          >
            {isToggleChecked ? tooltipMessageForSynced : tooltipMessageForNotSynced}
          </OwcTooltip>
          {!showCalendarIcon ? (
            <OwcIcon
              id="icon-warning"
              type="outlined"
              name="circle_warning"
              {...react18WorkaroundProps}
            />
          ) : (
            <OwcIcon
              id="icon-warning"
              {...react18WorkaroundProps}
            >
              <GoogleCalenderIcon />
            </OwcIcon>
          )}
        </div>
      )}
      {showDialog &&
        createPortal(
          <OwcModalDialog
            visible={showDialog}
            size="sm"
            className="owcmodalZIndex"
            {...react18WorkaroundProps}
            onVisibleChange={(e) => {
              if (!e.target.visible) {
                setShowDialog(false);
              }
            }}
            disableEscapeKeydown={true}
          >
            <div slot="header">Are you sure?</div>
            <div
              style={{
                paddingTop: THEME["one-spacer-24"],
                display: "flex",
                justifyContent: "center",
              }}
            >
              <OwcButton
                onClick={() => {
                  setShowDialog(false);
                  setLoggedIn("loading");
                  handleLogout();
                }}
                variant="secondary"
                style={{ marginRight: THEME["one-spacer-32"] }}
                {...react18WorkaroundProps}
              >
                Yes
              </OwcButton>
              <OwcButton
                onClick={() => setShowDialog(false)}
                variant="primary"
                {...react18WorkaroundProps}
              >
                No
              </OwcButton>
            </div>
          </OwcModalDialog>,
          document.body
        )}
    </div>
  );
};

export const GoogleSignInToggle = compose(
  connect(mapStateToProps, {
    loadUserInfo: loadUserInfoAction,
  }),
  withApollo
)(GoogleCalendarSwitch) as ComponentType<{ showCalendarIcon?: boolean }>;
